import React from "react";
import { XsmallText } from "../../lib/ui/components/typography/Typography";

const ACTIVE_BORDER = "border-[var(--primary-700)]";
const ACTIVE_TEXT = "text-[var(--primary-700)]";

function verticalItemClass(isActive) {
    return `border-l-2 pl-3 transition-colors ${
        isActive ? ACTIVE_BORDER : "border-transparent"
    }`;
}

function verticalButtonClass(isActive) {
    const base =
        "w-full rounded-md border-0 bg-transparent px-0 py-1.5 text-left text-small transition-colors";
    if (isActive) {
        return `${base} font-semibold ${ACTIVE_TEXT}`;
    }
    return `${base} font-medium text-gray-600 hover:text-gray-900`;
}

function horizontalButtonClass(isActive) {
    const base =
        "shrink-0 whitespace-nowrap rounded-none border-0 border-b-2 bg-transparent px-2 py-1.5 text-small transition-colors";
    if (isActive) {
        return `${base} ${ACTIVE_BORDER} font-semibold ${ACTIVE_TEXT}`;
    }
    return `${base} border-transparent font-medium text-gray-600 hover:text-gray-900`;
}

/**
 * In-page sub-navigation for sections within a tab (role Settings, Role Details).
 */
export default function JobSettingsSectionNav({
    sections,
    activeSectionId,
    onSectionSelect,
    className = "",
    orientation = "vertical",
    title,
    disabledSectionIds = [],
}) {
    if (!sections?.length) {
        return null;
    }

    const ariaLabel = title ? `${title} sections` : "Sections";

    if (orientation === "horizontal") {
        return (
            <nav aria-label={ariaLabel} className={className}>
                {title ? (
                    <XsmallText
                        weight="bold"
                        emphasis
                        className="mb-2 block uppercase tracking-wide"
                    >
                        {title}
                    </XsmallText>
                ) : null}
                <div className="flex gap-3 overflow-x-auto border-b border-gray-200 pb-0">
                    {sections.map(({ id, label }) => {
                        const isDisabled = disabledSectionIds.includes(id);
                        return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => onSectionSelect(id)}
                            disabled={isDisabled}
                            className={`${horizontalButtonClass(activeSectionId === id)}${isDisabled ? " cursor-not-allowed opacity-40" : ""}`}
                            data-cy={`section-nav-${id}`}
                        >
                            {label}
                        </button>
                        );
                    })}
                </div>
            </nav>
        );
    }

    return (
        <nav aria-label={ariaLabel} className={className}>
            {title ? (
                <XsmallText
                    weight="bold"
                    emphasis
                    className="mb-3 block uppercase tracking-wide"
                >
                    {title}
                </XsmallText>
            ) : null}
            <ul className="m-0 list-none space-y-1 p-0">
                {sections.map(({ id, label }) => {
                    const isActive = activeSectionId === id;
                    const isDisabled = disabledSectionIds.includes(id);
                    return (
                        <li key={id} className={verticalItemClass(isActive)}>
                            <button
                                type="button"
                                onClick={() => onSectionSelect(id)}
                                disabled={isDisabled}
                                className={`${verticalButtonClass(isActive)}${isDisabled ? " cursor-not-allowed opacity-40" : ""}`}
                                data-cy={`section-nav-${id}`}
                            >
                                {label}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
